import { Link, useParams } from "react-router-dom";
import { getTrainingPlan } from "../api/endpoints";
import { Card, EmptyState, ErrorMessage, PageHeading, Spinner } from "../components/ui";
import { useAsync } from "../hooks/useAsync";

export default function TrainingPlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, error, loading, reload } = useAsync(() => getTrainingPlan(id!), [id]);

  if (loading) return <Spinner />;
  if (error || !data) return <ErrorMessage error={error ?? new Error("Plan not found")} onRetry={reload} />;

  return (
    <div className="space-y-6" data-testid="training-plan-detail-page">
      <Link to="/training" className="text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100" data-testid="training-plan-back-link">← Back to training</Link>
      <PageHeading title={data.name} subtitle={data.raceName ? `Primary race: ${data.raceName}` : "No target race set"} />

      <div className="grid gap-6 xl:grid-cols-[1.5fr_1fr]">
        <Card className="p-6">
          <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Phases & workouts</h2>
          {data.phases.length === 0 ? (
            <div className="mt-4">
              <EmptyState title="No phases yet" description="Phases will appear here once the plan has been structured." testId="training-plan-phases-empty-state" />
            </div>
          ) : (
            <div className="mt-4 space-y-4" data-testid="training-plan-phases-panel">
              {data.phases.map((phase) => (
                <Card key={phase.id} className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{phase.name}</h3>
                      {phase.description && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{phase.description}</p>}
                    </div>
                    {phase.colour && <span className="rounded-full px-2 py-1 text-xs font-medium text-white" style={{ backgroundColor: phase.colour }}>{phase.colour}</span>}
                  </div>
                  <div className="mt-3 space-y-3">
                    {phase.workouts.map((workout) => (
                      <div key={workout.id} className="rounded-lg border border-gray-200 px-3 py-2 dark:border-[#2e2e2e]" data-testid="training-plan-workout-row">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{workout.name}</p>
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-[#252525] dark:text-gray-200">{workout.status}</span>
                        </div>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{workout.scheduledDate ?? "Unscheduled"} · {workout.workoutType}</p>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Races</h2>
          {data.races.length === 0 ? (
            <div className="mt-4">
              <EmptyState title="No races attached" description="Attach a race to the plan to surface effort and countdown details." testId="training-plan-races-empty-state" />
            </div>
          ) : (
            <div className="mt-4 space-y-3" data-testid="training-plan-races-panel">
              {data.races.map((race) => (
                <div key={race.id} className="rounded-lg border border-gray-200 p-3 dark:border-[#2e2e2e]">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{race.name}</p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{race.raceDate} · {race.priorityLabel}</p>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{race.distanceKm ?? 0} km effort · {race.elevationM ?? 0} m climb</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
